import React from 'react';
import { RCSkeleton, RCImage, RCText } from '../';
import { WrapperElement, WrapperElementFunction } from '../helpers/WrapperElement';

interface RCProductCardProps {
  title: string;

  /** Uses the Eyebrow text style. Appears between the image and title. */
  eyebrow: string;

  /** Url string to be passed to RCImage. */
  imageUrl: string;

  /**
   * Accepts a function that returns `children` wrapped by a `<button>` or link element.
   *
   * Example:
   *
   * ```tsx
   * <RCProductCard
   *   ...
   *   wrapper={(children) => (
   *     <CoreLink to="/where/ever/you/want/">
   *       {children}
   *     </CoreLink>
   *   )}
   * />
   * ```
  */
  wrapper?: WrapperElementFunction;
}

/**
 * A card component for representing "Products".
 *
 * This component accepts child content and renders it at the bottom of the card.
 * Use this area for nudges, CTAs, etc.
 */
export function RCProductCard({
  title,
  eyebrow,
  imageUrl,
  wrapper = undefined,
  children = undefined,
}: React.PropsWithChildren<RCProductCardProps>) {

  return (
    <div
      className="rc-product-card"
      data-rc-image-overlay-trigger
    >
      <WrapperElement
        element={wrapper}
        className="rc-product-card__wrapper-element"
      >
        <div className="rc-product-card__image-wrapper">
          <RCImage
            src={imageUrl}
            overlay="offwhiteWithHover"
            borderRadius="md"
          />
        </div>
        <div className="rc-product-card__content">
          <div>
            <div className="rc-product-card__eyebrow">
              <RCText.Eyebrow maxLines={1}>{eyebrow}</RCText.Eyebrow>
            </div>
            <div className="rc-product-card__title">
              {title}
            </div>
          </div>
          <div className="rc-product-card__footer">
            {children}
          </div>
        </div>
      </WrapperElement>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rc-product-card">
      <div className="rc-product-card__image-wrapper">
        <RCSkeleton width="100%" height="100%" />
      </div>
      <div className="rc-product-card__content">
        <div>
          <div className="text-eyebrow-300 mb-1">
            <RCSkeleton width="25%" />
          </div>
          <div className="rc-product-card__title">
            <RCSkeleton width="75%" />
          </div>
        </div>
      </div>
    </div>
  );
}

RCProductCard.Skeleton = Skeleton;
